import { Component, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  CreateEnfermedadesGravesPlanDto,
  CreateEnfermedadesGravesPremiumDto,
  CoverageListDto,
} from '../../../../core/data/new-dtos/insurance-type.dtos';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { CoveragesService } from '../../services/coverages.service';
import { CoverageModalComponent, CoverageModalResult } from '../../../coverages/ui/coverage-modal/coverage-modal.component';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-serious-illness-plan-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMaskDirective,
    NgbTooltipModule,
    NgSelectModule,
    CoverageModalComponent,
  ],
  templateUrl: './serious-illness-plan-selector.component.html',
  styleUrl: './serious-illness-plan-selector.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SeriousIllnessPlanSelectorComponent),
      multi: true,
    },
    provideNgxMask(),
  ],
})
export class SeriousIllnessPlanSelectorComponent
  implements ControlValueAccessor, OnInit {
  form: FormGroup;
  availableCoverages: CoverageListDto[] = [];
  loading = false;
  showCoverageModal = false;
  activePlanIndex: number | null = null;

  private isWriteValue = false;

  constructor(
    private fb: FormBuilder,
    private coveragesService: CoveragesService,
  ) {
    this.form = this.fb.group({
      plans: this.fb.array([]),
    });

    this.form.valueChanges.subscribe((val) => {
      if (!this.isWriteValue) {
        this.onChange(val.plans);
        this.onTouched();
      }
    });
  }

  async ngOnInit() {
    await this.loadCoverages();
  }

  async loadCoverages() {
    this.loading = true;
    try {
      this.availableCoverages = await lastValueFrom(this.coveragesService.getAll());
    } catch (e) {
      console.error('Error loading coverages', e);
    } finally {
      this.loading = false;
    }
  }

  get plans(): FormArray {
    return this.form.get('plans') as FormArray;
  }

  getPremiums(planIndex: number): FormArray {
    return this.plans.at(planIndex).get('premiums') as FormArray;
  }

  getCoverageIds(planIndex: number): number[] {
    return this.plans.at(planIndex).get('coverageIds')?.value || [];
  }

  onCoverageIdsChange(planIndex: number, ids: number[]) {
    this.plans.at(planIndex).get('coverageIds')?.setValue(ids);
  }

  openCoverageModal(planIndex: number) {
    this.activePlanIndex = planIndex;
    this.showCoverageModal = true;
  }

  async onCoverageModalClosed(result: CoverageModalResult) {
    this.showCoverageModal = false;
    if (result.action === 'save' && result.data) {
      this.loading = true;
      try {
        const newId = await lastValueFrom(
          this.coveragesService.create(result.data as any),
        );
        await this.loadCoverages();

        if (this.activePlanIndex !== null) {
          const current = this.getCoverageIds(this.activePlanIndex);
          this.onCoverageIdsChange(this.activePlanIndex, [...current, newId]);
        }
      } catch (e) {
        console.error('Error creating coverage', e);
      } finally {
        this.loading = false;
        this.activePlanIndex = null;
      }
    } else {
      this.activePlanIndex = null;
    }
  }

  createPlanGroup(plan?: any): FormGroup {
    return this.fb.group({
      name: [plan?.name || '', Validators.required],
      diagnosisPayout: [
        plan?.diagnosisPayout || 0,
        [Validators.required, Validators.min(0)],
      ],
      survivalPayout: [
        plan?.survivalPayout || 0,
        [Validators.required, Validators.min(0)],
      ],
      survivalPeriodMonths: [
        plan?.survivalPeriodMonths || 0,
        [Validators.required, Validators.min(0)],
      ],
      premiums: this.fb.array(
        plan?.premiums?.map((p: any) => this.createPremiumGroup(p)) || [],
      ),
      coverageIds: [plan?.coverageIds || []],
    });
  }

  createPremiumGroup(premium?: CreateEnfermedadesGravesPremiumDto): FormGroup {
    return this.fb.group({
      minAge: [premium?.minAge || 0, [Validators.required, Validators.min(0)]],
      maxAge: [premium?.maxAge || 0, [Validators.required, Validators.min(0)]],
      amount: [premium?.amount || 0, [Validators.required, Validators.min(0)]],
    });
  }

  addPlan() {
    this.plans.push(this.createPlanGroup());
  }

  removePlan(index: number) {
    this.plans.removeAt(index);
  }

  addPremium(planIndex: number) {
    this.getPremiums(planIndex).push(this.createPremiumGroup());
  }

  removePremium(planIndex: number, premiumIndex: number) {
    this.getPremiums(planIndex).removeAt(premiumIndex);
  }

  calculateMonthlyPayout(planIndex: number): number {
    const plan = this.plans.at(planIndex);
    const survivalPayout = Number(plan.get('survivalPayout')?.value || 0);
    const months = Number(plan.get('survivalPeriodMonths')?.value || 0);

    if (months === 0) return 0;
    return survivalPayout / months;
  }

  writeValue(obj: any): void {
    if (obj && Array.isArray(obj)) {
      this.isWriteValue = true;
      this.plans.clear();
      obj.forEach((plan) => {
        this.plans.push(this.createPlanGroup(plan));
      });
      this.isWriteValue = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  private onChange: any = () => { };
  private onTouched: any = () => { };
}
